import { useQuery } from '@apollo/client/react';
import { Box, Skeleton, Paper, Typography, Stack } from '@mui/material';
import { Types, ssrApolloService, useRouteInfo } from '@scorenco/core';
import { NextPageWithLayout } from '@scorenco/next';

import { PageLayout, PageSeo, SportChip } from '@/components';
import { WebLayout } from '@/layouts';
import { RouteKey } from '@/routes';
import { getSport } from '@/utils';

import { ClubList } from './_internal/ClubList';
import { GetDepartmentByCodeDocument } from './_internal/Clubs.query.generated';
import { DepartmentList } from './_internal/DepartmentList';

const Clubs: NextPageWithLayout<{ sport: Types.Clubs_Sport }> = ({ sport }) => {
  const routeInfo = useRouteInfo();
  const sportSlug = routeInfo.query.sport as string;
  const departmentCode = routeInfo.query.department;

  const sportName = sport?.name || sportSlug;

  // Récupère les infos d'un département
  const departmentByCodeQuery = useQuery(GetDepartmentByCodeDocument, {
    context: {
      role: 'anonymous',
    },
    variables: {
      departmentCode: departmentCode as string,
    },
    skip: !departmentCode,
  });
  const departmentByCode = departmentByCodeQuery.data?.places_department?.[0];
  const departmentName = departmentByCode?.name;

  return (
    <Box>
      <PageSeo
        title={`Clubs de ${sportName} - ${departmentName} : résultats, classements, calendrier des clubs de votre département`}
        description={`Découvrez les clubs de ${sportName} dans le département de ${departmentName} sur Score'n'co🔵🟢. Suivez les résultats et les classements des équipes de ${sportName} dans votre région. Restez informés pour ne rien manquer de l'action sportive près de chez vous.`}
        url={{
          routeKey: RouteKey.Sport,
          params: {
            sport: sportSlug,
          },
        }}
      />

      <PageLayout
        image={sport?.image}
        breadcrumbs={
          departmentCode
            ? [
                {
                  label: sport?.name || '',
                  href: {
                    routeKey: RouteKey.Sport,
                    params: { sport: sportSlug },
                  },
                },
                {
                  label: 'Clubs',
                  href: {
                    routeKey: RouteKey.Clubs,
                    params: { sport: sportSlug },
                  },
                },
                {
                  label: departmentByCode?.name || '',
                  current: true,
                },
              ]
            : [
                {
                  label: sport?.name || '',
                  href: {
                    routeKey: RouteKey.Sport,
                    params: { sport: sportSlug },
                  },
                },
                {
                  label: 'Clubs',
                  current: true,
                },
              ]
        }
      >
        <Paper variant="bordered" sx={{ mb: 3 }}>
          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
            spacing={4}
            width="100%"
            alignItems="center"
          >
            <Box
              textAlign={{
                xs: 'center',
                sm: 'left',
              }}
            >
              {departmentCode && (
                <SportChip
                  sport={sport?.slug}
                  sx={{
                    mb: 2,
                  }}
                />
              )}
              <Typography variant="h1" component="h1">
                {departmentCode
                  ? departmentByCode?.name || <Skeleton width={200} />
                  : `${sport?.emoji} ${sport?.name}`}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {departmentCode ? (
          <ClubList sportSlug={sportSlug} departmentCode={departmentCode} />
        ) : (
          <DepartmentList sportSlug={sportSlug} />
        )}
      </PageLayout>
    </Box>
  );
};

export const getServerSideProps = WebLayout.buildGetServerSideProps({}, [
  async ({ query }, previous) => {
    const { apolloClient, addApolloState } = ssrApolloService();

    const sportSlug = query.sport;
    const departmentCode = query.department;

    if (typeof sportSlug !== 'string') {
      return { notFound: true };
    }

    if (query['no-ssr'] === undefined) {
      const sport = await getSport(sportSlug);
      if (!sport) {
        return { notFound: true };
      }

      if (typeof departmentCode !== 'string') {
        await DepartmentList.ssrQuery({ apolloClient });
      } else {
        await apolloClient.query({
          query: GetDepartmentByCodeDocument,
          variables: { departmentCode },
        });
        await ClubList.ssrQuery({
          apolloClient,
          variables: { sportSlug, departmentCode },
        });
      }

      return addApolloState({
        ...previous,
        props: {
          ...previous.props,
          sport,
        },
      });
    }

    return addApolloState(previous);
  },
]);

Clubs.getLayout = WebLayout.buildGetLayout({
  strapiMenu: true,
  amplify: { protectedPage: false },
});

export default Clubs;
